import React, { useState, useCallback, useRef, useEffect } from 'react';
import * as signalR from '@microsoft/signalr';
import VenueSelector from './components/VenueSelector';
import VenueCreator from './components/VenueCreator';
import HomePage from './components/HomePage';
import EventSelector from './components/EventSelector';
import EventCreator from './components/EventCreator';
import EventManagement from './components/EventManagement';
import TicketingSeatMap from './components/TicketingSeatMap';
import CheckoutForm from './components/CheckoutForm';
import TicketView from './components/TicketView';
import QRScanner from './components/QRScanner';
import VenuePhotoUpload from './components/VenuePhotoUpload';
import AdminPanel from './components/AdminPanel';
import InvitationCodeManager from './components/InvitationCodeManager';
import EventAccessWithCode from './components/EventAccessWithCode';
import UserProfile from './components/UserProfile';
import { api } from './services/api';
import { toast } from './utils/swal';
import GiseMaskot from './components/GiseMaskot';
import Login from './components/Login';
import Register from './components/Register';
import { ForgotPassword, ResetPassword } from './components/ForgotPassword';
import { AuthProvider, useAuth } from './components/AuthContext';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const SEAT_TIMEOUT_MS = 5 * 60 * 1000; // 5 dakika

// ── sessionStorage yardımcı fonksiyonlar ──
const SS_KEY = 'gg_pending_checkout';

const savePendingCheckout = (data) => {
  try { sessionStorage.setItem(SS_KEY, JSON.stringify({ ...data, savedAt: Date.now() })); } catch { }
};

const loadPendingCheckout = () => {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.savedAt > 10 * 60 * 1000) { sessionStorage.removeItem(SS_KEY); return null; }
    return data;
  } catch { return null; }
};

const clearPendingCheckout = () => {
  try { sessionStorage.removeItem(SS_KEY); } catch { }
};

// ── App-level SignalR seating hook ──
// Bağlantı App.js'de yaşar — view değişse de kopmaz
function useAppSeating(eventId) {
  const [seats, setSeats]             = useState({});
  const [mySeats, setMySeats]         = useState(new Set());
  const [isConnected, setIsConnected] = useState(false);
  const [seatTimers, setSeatTimers]   = useState({});

  const connectionRef    = useRef(null);
  const seatExpiryRef    = useRef({});
  const timerIntervalRef = useRef(null);
  const currentEventRef  = useRef(eventId);

  // Saniye bazlı countdown
  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const updated = {};
      let hasAny = false;
      Object.entries(seatExpiryRef.current).forEach(([seatId, expiresAt]) => {
        updated[seatId] = Math.max(0, Math.ceil((expiresAt - now) / 1000));
        hasAny = true;
      });
      if (hasAny) setSeatTimers({ ...updated });
    }, 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, []);

  // Bağlantıyı kur — sadece eventId değişince yeniden bağlan
  useEffect(() => {
    if (!eventId) return;
    currentEventRef.current = eventId;

    // Önceki bağlantıyı temizle
    if (connectionRef.current) {
      connectionRef.current.stop();
      connectionRef.current = null;
    }

    // Koltukları sıfırla (yeni etkinlik)
    setSeats({});
    setMySeats(new Set());
    setSeatTimers({});
    seatExpiryRef.current = {};

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/seathub`)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    conn.on('InitialSeats', (initialSeats) => setSeats(initialSeats || {}));

    conn.on('SeatSelected', (seatId, userId) => {
      setSeats(prev => ({ ...prev, [seatId]: { userId, status: 'temp' } }));
    });

    conn.on('SeatConfirmed', (seatId, userId) => {
      setSeats(prev => ({ ...prev, [seatId]: { userId, status: 'confirmed' } }));
    });

    conn.on('SeatReleased', (seatId) => {
      setSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      setMySeats(prev => { const s = new Set(prev); s.delete(seatId); return s; });
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
    });

    conn.onreconnecting(() => setIsConnected(false));
    conn.onreconnected(() => {
      setIsConnected(true);
      conn.invoke('JoinEvent', currentEventRef.current);
    });
    conn.onclose(() => setIsConnected(false));

    conn.start()
      .then(() => {
        setIsConnected(true);
        return conn.invoke('JoinEvent', eventId);
      })
      .catch(err => {
        console.error('SignalR bağlantı hatası:', err);
        setIsConnected(false);
      });

    connectionRef.current = conn;

    return () => {
      // Sadece component tamamen unmount olunca durdur (App unmount = sayfa kapandı)
      // View değişimlerinde bu cleanup çalışmaz çünkü eventId aynı kalır
    };
  }, [eventId]);

  // Etkinlik değişince veya komponent kapanınca bağlantıyı durdur
  useEffect(() => {
    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      clearInterval(timerIntervalRef.current);
    };
  }, []);

  const selectSeat = useCallback(async (seatId) => {
    const conn = connectionRef.current;
    if (!conn || !isConnected) return false;
    try {
      const success = await conn.invoke('SelectSeat', currentEventRef.current, seatId);
      if (success) {
        setMySeats(prev => new Set(prev).add(seatId));
        seatExpiryRef.current[seatId] = Date.now() + SEAT_TIMEOUT_MS;
        setSeatTimers(prev => ({ ...prev, [seatId]: Math.ceil(SEAT_TIMEOUT_MS / 1000) }));
      }
      return success;
    } catch { return false; }
  }, [isConnected]);

  const confirmSeat = useCallback(async (seatId) => {
    const conn = connectionRef.current;
    if (!conn || !isConnected) return;
    try {
      await conn.invoke('ConfirmSeat', currentEventRef.current, seatId);
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
    } catch { }
  }, [isConnected]);

  const releaseSeat = useCallback(async (seatId) => {
    const conn = connectionRef.current;
    if (!conn || !isConnected) return false;
    try {
      await conn.invoke('ReleaseSeat', currentEventRef.current, seatId);
      setMySeats(prev => { const s = new Set(prev); s.delete(seatId); return s; });
      setSeats(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      delete seatExpiryRef.current[seatId];
      setSeatTimers(prev => { const n = { ...prev }; delete n[seatId]; return n; });
      return true;
    } catch { return false; }
  }, [isConnected]);

  const releaseAllMySeats = useCallback(async () => {
    for (const seatId of [...mySeats]) {
      await releaseSeat(seatId);
    }
  }, [mySeats, releaseSeat]);

  const minSecondsLeft = Object.values(seatTimers).length > 0
    ? Math.min(...Object.values(seatTimers))
    : null;

  return {
    seats, mySeats, selectSeat, confirmSeat, releaseSeat,
    releaseAllMySeats, isConnected, seatTimers, minSecondsLeft,
  };
}

// ── Ana içerik bileşeni ──
function AppContent() {
  const { isLoggedIn, user, logout } = useAuth();

  const resetToken = new URLSearchParams(window.location.search).get('token');

  // Mail linkinden gelen public bilet erişimi: /bilet/{id}?t=TOKEN
  const _biletMatch = window.location.pathname.match(/^\/bilet\/([^/?]+)/);
  const publicTicketId    = _biletMatch ? _biletMatch[1] : null;
  const publicTicketToken = publicTicketId
    ? new URLSearchParams(window.location.search).get('t')
    : null;

  const [authView, setAuthView]       = useState('home');
  const [currentView, setCurrentView] = useState(
    publicTicketId && publicTicketToken ? 'public-ticket' : 'home'
  );
  const [publicTicketRes, setPublicTicketRes] = useState(
    publicTicketId && publicTicketToken ? { id: publicTicketId, token: publicTicketToken } : null
  );

  const [selectedVenue, setSelectedVenue]               = useState(null);
  const [selectedEvent, setSelectedEvent]               = useState(null);
  const [selectedSeatsWithTypes, setSelectedSeatsWithTypes] = useState({});
  const [checkoutData, setCheckoutData]                 = useState(null);
  const [completedReservation, setCompletedReservation] = useState(null);
  const [showCreator, setShowCreator]                   = useState(false);
  const [editingVenue, setEditingVenue]                 = useState(null);
  const [editingEvent, setEditingEvent]                 = useState(null);
  const [photoUploadVenue, setPhotoUploadVenue]         = useState(null);
  const [showCodeAccess, setShowCodeAccess]             = useState(false);
  const [codeAccessEvent, setCodeAccessEvent]           = useState(null);
  const [invitationCode, setInvitationCode]             = useState(null);

  // SignalR — App seviyesinde yaşar
  const seating = useAppSeating(selectedEvent?.id || null);

  // ── Handler'lar ──

  const handlePublicEventSelect = async (event) => {
    setCodeAccessEvent(null);
    setSelectedEvent(event);
    setSelectedSeatsWithTypes({});
    try {
      const venue = await api.getVenue(event.venueId);
      setSelectedVenue(venue);
      setCurrentView('seat-select');
      setAuthView('home');
    } catch {
      toast.error('Salon bilgisi yüklenemedi.');
    }
  };

  const handlePublicCodeAccess = (event) => {
    setCodeAccessEvent(event);
    setShowCodeAccess(true);
  };

  const handlePublicCodeSuccess = async (event, code, meta = {}) => {
    setShowCodeAccess(false);
    setCodeAccessEvent(null);
    const ic = {
      ...code,
      isReadOnly:    meta.isReadOnly    || false,
      existingSeats: meta.existingSeats || [],
      reservationId: meta.reservationId || null,
    };
    setInvitationCode(ic);
    if (meta.isReadOnly && meta.reservationId) {
      setCompletedReservation({ id: meta.reservationId });
      setCurrentView('tickets');
      setAuthView('home');
      return;
    }
    try {
      const venue = await api.getVenue(event.venueId);
      setSelectedVenue(venue);
      setSelectedEvent(event);
      setSelectedSeatsWithTypes({});
      setCurrentView('seat-select');
      setAuthView('home');
    } catch {
      toast.error('Salon bilgisi yüklenemedi.');
    }
  };

  const handleVenueCreated = (newVenue) => {
    setShowCreator(false);
    setEditingVenue(null);
    setSelectedVenue(newVenue);
    setCurrentView('venue-manage');
  };

  const handleEditVenue   = (venue) => { setEditingVenue(venue); setShowCreator(true); };
  const handleCancelCreator = () => { setShowCreator(false); setEditingVenue(null); };

  const handleEventSelect = async (event) => {
    setSelectedEvent(event);
    setSelectedSeatsWithTypes({});
    try {
      const venue = await api.getVenue(event.venueId);
      setSelectedVenue(venue);
      setCurrentView('seat-select');
    } catch { console.error('Salon yüklenemedi'); }
  };

  // Checkout'a geç — view değişir ama SignalR bağlantısı KOPMAZ
  const handleCheckout = (data) => {
    const cd = {
      ...data,
      invitationCode: invitationCode?.code || null,
      minSecondsLeft: seating.minSecondsLeft,
    };

    if (!isLoggedIn()) {
      savePendingCheckout({
        event: selectedEvent,
        venue: selectedVenue,
        checkoutData: cd,
        invitationCode,
        step: 'checkout',
      });
      setAuthView('login');
      return;
    }

    setCheckoutData(cd);
    setCurrentView('checkout');
    // SignalR bağlantısı burada KOPMAZ — sadece view değişti
  };

  const handleCheckoutComplete = (reservation) => {
    clearPendingCheckout();
    // Koltukları confirm et
    for (const seatId of [...seating.mySeats]) {
      seating.confirmSeat(seatId);
    }
    setSelectedSeatsWithTypes({});
    setCompletedReservation(reservation);
    setCurrentView('tickets');
  };

  // Checkout'tan geri — SignalR kopmaz, koltuk seçimleri korunur
  const handleBackFromCheckout = () => {
    setCurrentView('seat-select');
    // Koltuklar hâlâ mySeats'te, harita açılınca seçili görünür
  };

  const handleBackToHome = async () => {
    clearPendingCheckout();
    // Seçili koltuklar varsa serbest bırak
    if (seating.mySeats.size > 0) {
      await seating.releaseAllMySeats();
    }
    setSelectedSeatsWithTypes({});
    setCurrentView('home');
    setSelectedVenue(null);
    setSelectedEvent(null);
    setCheckoutData(null);
    setCompletedReservation(null);
    setShowCreator(false);
    setEditingVenue(null);
    setEditingEvent(null);
    setPhotoUploadVenue(null);
    setShowCodeAccess(false);
    setCodeAccessEvent(null);
    setInvitationCode(null);
  };

  const handlePhotoUpload         = (venue) => { setPhotoUploadVenue(venue); setCurrentView('photo-upload'); };
  const handlePhotoUploadComplete = ()      => { setPhotoUploadVenue(null); setCurrentView('venue-manage'); };

  const handleVenueAction = (venue) => {
    setSelectedVenue(venue);
    const action = window.prompt('Salon için ne yapmak istersiniz?\n\n1 - Fotoğraf Yükle\n2 - Salon Düzenle\n3 - İptal\n\nSeçiminizi girin:');
    if (action === '1') handlePhotoUpload(venue);
    else if (action === '2') handleEditVenue(venue);
  };

  const handleCodeAccess = (event) => { setCodeAccessEvent(event); setShowCodeAccess(true); };

  const handleCodeSuccess = async (event, code, meta = {}) => {
    setShowCodeAccess(false);
    setCodeAccessEvent(null);
    setInvitationCode({
      ...code,
      isReadOnly:    meta.isReadOnly    || false,
      existingSeats: meta.existingSeats || [],
      reservationId: meta.reservationId || null,
    });
    if (meta.isReadOnly && meta.reservationId) {
      setCompletedReservation({ id: meta.reservationId });
      setCurrentView('tickets');
    } else {
      await handleEventSelect(event);
    }
  };

  // ── Şifre sıfırlama ──
  if (resetToken) {
    return (
      <ResetPassword
        token={resetToken}
        onSuccess={() => { window.history.replaceState({}, '', '/'); setAuthView('login'); }}
      />
    );
  }

  // ── Auth panelleri ──
  if (!isLoggedIn() && authView === 'register') {
    return (
      <Register
        onRegisterSuccess={(user, pending) => {
          if (pending) {
            setSelectedVenue(pending.venue);
            setSelectedEvent(pending.event);
            setCheckoutData(pending.checkoutData);
            setInvitationCode(pending.invitationCode || null);
            setCurrentView('checkout');
            setAuthView('home');
          } else {
            setAuthView('login');
          }
        }}
        onGoToLogin={() => setAuthView('login')}
      />
    );
  }

  if (!isLoggedIn() && authView === 'forgot') {
    return <ForgotPassword onBack={() => setAuthView('login')} />;
  }

  if (!isLoggedIn() && authView === 'login') {
    return (
      <Login
        onLoginSuccess={async () => {
          const pending = loadPendingCheckout();
          if (pending) {
            setSelectedVenue(pending.venue);
            setSelectedEvent(pending.event);
            setCheckoutData(pending.checkoutData);
            setInvitationCode(pending.invitationCode || null);
            setCurrentView('checkout');
          } else {
            setCurrentView('home');
          }
          setAuthView('home');
        }}
        onGoToRegister={() => setAuthView('register')}
        onForgotPassword={() => setAuthView('forgot')}
      />
    );
  }

  // ── Public Ana Sayfa ──
  if (!isLoggedIn() && authView === 'home') {

    // Mail linkinden gelen public bilet — login gerekmez
    if (currentView === 'public-ticket' && publicTicketRes) {
      return (
        <>
          <GiseMaskot currentView="tickets" />
          <TicketView
            reservationId={publicTicketRes.id}
            ticketToken={publicTicketRes.token}
            onBack={() => {
              window.history.replaceState({}, '', '/');
              setCurrentView('home');
              setPublicTicketRes(null);
            }}
          />
        </>
      );
    }

    if (currentView === 'seat-select' && selectedEvent && selectedVenue) {
      return (
        <>
          <GiseMaskot currentView="seat-select" />
          <TicketingSeatMap
            venue={selectedVenue}
            event={selectedEvent}
            invitationCode={invitationCode}
            // SignalR props — App'ten geliyor
            seats={seating.seats}
            mySeats={seating.mySeats}
            selectSeat={seating.selectSeat}
            releaseSeat={seating.releaseSeat}
            confirmSeat={seating.confirmSeat}
            isConnected={seating.isConnected}
            minSecondsLeft={seating.minSecondsLeft}
            selectedSeatsWithTypes={selectedSeatsWithTypes}
            setSelectedSeatsWithTypes={setSelectedSeatsWithTypes}
            onBack={() => setCurrentView('home')}
            onCheckout={handleCheckout}
            onGoHome={() => {
              clearPendingCheckout();
              seating.releaseAllMySeats();
              setSelectedSeatsWithTypes({});
              setCurrentView('home');
              setSelectedVenue(null);
              setSelectedEvent(null);
              setInvitationCode(null);
            }}
          />
        </>
      );
    }

    if (currentView === 'tickets' && completedReservation) {
      return (
        <>
          <GiseMaskot currentView="tickets" />
          <TicketView
            reservationId={completedReservation.id}
            onBack={() => { setCurrentView('home'); setCompletedReservation(null); }}
          />
        </>
      );
    }

    if (currentView === 'checkout') {
      return (
        <>
          <GiseMaskot currentView="checkout" />
          <CheckoutForm
            checkoutData={checkoutData}
            minSecondsLeft={seating.minSecondsLeft}
            onBack={handleBackFromCheckout}
            onComplete={handleCheckoutComplete}
            onGoHome={handleBackToHome}
          />
        </>
      );
    }

    return (
      <>
        <GiseMaskot currentView="home" />
        <HomePage
          isLoggedIn={false}
          user={null}
          onEventSelect={handlePublicEventSelect}
          onCodeAccess={handlePublicCodeAccess}
          onGoToLogin={() => setAuthView('login')}
          onGoToRegister={() => setAuthView('register')}
        />
        {showCodeAccess && codeAccessEvent && (
          <EventAccessWithCode
            event={codeAccessEvent}
            onSuccess={handlePublicCodeSuccess}
            onCancel={() => { setShowCodeAccess(false); setCodeAccessEvent(null); }}
          />
        )}
      </>
    );
  }

  // ── Login olmuş kullanıcı VIEW ROUTER ──
  const headerNavProps = {
    onGoToProfile:         () => setCurrentView('profile'),
    onGoToEventManagement: () => setCurrentView('event-management'),
    onGoToAdminPanel:      () => setCurrentView('admin-panel'),
    onGoToVenueManage:     () => setCurrentView('venue-manage'),
    onGoToQrScanner:       () => setCurrentView('qr-scanner'),
  };

  if (currentView === 'home') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <HomePage
          isLoggedIn={true}
          user={user}
          onEventSelect={handleEventSelect}
          onCodeAccess={handleCodeAccess}
          onGoToLogin={() => setAuthView('login')}
          onGoToRegister={() => setAuthView('register')}
          onLogout={logout}
          onGoToProfile={() => setCurrentView('profile')}
          onGoToEventManagement={() => setCurrentView('event-management')}
          onGoToAdminPanel={() => setCurrentView('admin-panel')}
          onGoToQrScanner={() => setCurrentView('qr-scanner')}
          onGoToVenueManage={() => setCurrentView('venue-manage')}
        />
        {showCodeAccess && codeAccessEvent && (
          <EventAccessWithCode
            event={codeAccessEvent}
            onSuccess={handleCodeSuccess}
            onCancel={() => { setShowCodeAccess(false); setCodeAccessEvent(null); }}
          />
        )}
      </>
    );
  }

  if (currentView === 'event-management') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <EventManagement
          onBack={handleBackToHome}
          onCreateNew={() => { setEditingEvent(null); setCurrentView('event-creator'); }}
          onEdit={(event) => { setEditingEvent(event); setCurrentView('event-creator'); }}
          {...headerNavProps}
        />
      </>
    );
  }

  if (currentView === 'event-creator') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <EventCreator
          onBack={() => setCurrentView('event-management')}
          onEventCreated={(createdEvent) => {
            setEditingEvent(null);
            setCurrentView('event-management');
            if (!editingEvent && createdEvent?.id) {
              toast.success('Etkinlik oluşturuldu! Fotoğraf eklemek için kamera butonunu kullanın.');
            }
          }}
          editEvent={editingEvent}
          {...headerNavProps}
        />
      </>
    );
  }

  if (currentView === 'admin-panel') return <AdminPanel onBack={handleBackToHome} {...headerNavProps} />;
  if (currentView === 'qr-scanner') return <QRScanner onBack={handleBackToHome} />;

  if (currentView === 'photo-upload' && photoUploadVenue) {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <VenuePhotoUpload
          venue={photoUploadVenue}
          onBack={() => setCurrentView('venue-manage')}
          onUpdate={(updatedVenue) => setPhotoUploadVenue(updatedVenue)}
          {...headerNavProps}
        />
      </>
    );
  }

  if (currentView === 'venue-manage') {
    if (showCreator) {
      return <VenueCreator onVenueCreated={handleVenueCreated} onCancel={handleCancelCreator} editVenue={editingVenue} />;
    }
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <div className="venue-management">
          <VenueSelector
            onVenueSelect={handleVenueAction}
            onCreateNew={() => setShowCreator(true)}
            onEditVenue={handleEditVenue}
            onBack={handleBackToHome}
            {...headerNavProps}
          />
        </div>
      </>
    );
  }

  if (currentView === 'event-select') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <EventSelector
          onEventSelect={handleEventSelect}
          onCodeAccess={handleCodeAccess}
          onBack={handleBackToHome}
        />
        {showCodeAccess && codeAccessEvent && (
          <EventAccessWithCode
            event={codeAccessEvent}
            onSuccess={handleCodeSuccess}
            onCancel={() => { setShowCodeAccess(false); setCodeAccessEvent(null); }}
          />
        )}
      </>
    );
  }

  if (currentView === 'seat-select') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <TicketingSeatMap
          venue={selectedVenue}
          event={selectedEvent}
          invitationCode={invitationCode}
          // SignalR props — App'ten geliyor
          seats={seating.seats}
          mySeats={seating.mySeats}
          selectSeat={seating.selectSeat}
          releaseSeat={seating.releaseSeat}
          confirmSeat={seating.confirmSeat}
          isConnected={seating.isConnected}
          minSecondsLeft={seating.minSecondsLeft}
          selectedSeatsWithTypes={selectedSeatsWithTypes}
          setSelectedSeatsWithTypes={setSelectedSeatsWithTypes}
          onBack={async () => {
            if (seating.mySeats.size > 0) {
              await seating.releaseAllMySeats();
              setSelectedSeatsWithTypes({});
            }
            setCurrentView('event-select');
          }}
          onCheckout={handleCheckout}
          onGoHome={handleBackToHome}
        />
      </>
    );
  }

  if (currentView === 'checkout') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <CheckoutForm
          checkoutData={checkoutData}
          minSecondsLeft={seating.minSecondsLeft}
          onBack={handleBackFromCheckout}
          onComplete={handleCheckoutComplete}
          onGoHome={handleBackToHome}
        />
      </>
    );
  }

  if (currentView === 'profile') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <UserProfile
          onBack={handleBackToHome}
          onViewTicket={(reservationId) => {
            setCompletedReservation({ id: reservationId });
            setCurrentView('tickets-from-profile');
          }}
        />
      </>
    );
  }

  // Profil üzerinden bilet görüntüleme — geri profile döner
  if (currentView === 'tickets-from-profile' && completedReservation) {
    return (
      <>
        <GiseMaskot currentView="tickets" />
        <TicketView
          reservationId={completedReservation.id}
          onBack={() => setCurrentView('profile')}
        />
      </>
    );
  }

  // Public bilet erişimi — mail linkinden, login gerektirmez
  if (currentView === 'public-ticket' && publicTicketRes) {
    return (
      <>
        <GiseMaskot currentView="tickets" />
        <TicketView
          reservationId={publicTicketRes.id}
          ticketToken={publicTicketRes.token}
          onBack={() => {
            window.history.replaceState({}, '', '/');
            setCurrentView('home');
            setPublicTicketRes(null);
          }}
        />
      </>
    );
  }

  if (currentView === 'tickets') {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <TicketView
          reservationId={completedReservation.id}
          onBack={handleBackToHome}
        />
      </>
    );
  }

  if (showCreator) {
    return (
      <>
        <GiseMaskot currentView={currentView} />
        <VenueCreator
          onVenueCreated={handleVenueCreated}
          onCancel={handleBackToHome}
          editVenue={editingVenue}
        />
      </>
    );
  }

  return <GiseMaskot currentView={currentView} />;
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;